import { useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Copy, Check, FileDown, ImageDown } from "lucide-react";
import { lineColor, lineInk } from "@/lib/lineColors";
import { ADORNMENTS } from "@/lib/adornments";
import AdornmentsSection from "@/components/AdornmentsSection";
import MysticMarkings from "@/components/MysticMarkings";
import ShareMark from "@/components/ShareMark";
import { HANDS_GRATITUDE, PALMISTRY_RESPECT } from "@/lib/lore";
import { buildReadingPdf, buildMarkImageBlob, downloadBlob, slug } from "@/lib/keepsakeArtifacts";

const SOMA_FOOTER =
  "Part of the Soma family — Lumin (aura) · Lixir (tea leaves) · Linea (palms).";

function buildIntro(name) {
  const who = name ? `${name}, thank you` : "Thank you";
  return `${who} for letting us sit with the story in your palms. What follows isn't a prediction — it's a pattern. A few lines your hands already carry, offered as an invitation to notice, not to know.`;
}

const CLOSING =
  "Sit with whatever resonates. Leave the rest. These lines have been forming for a lifetime, and they'll still be here tomorrow — they're yours.\n\nWith warmth,\nSoma Linea";

export default function TakeHomeReport({ open, onClose, onFinish, guestName, prints, lines, markings, overall, compatibility }) {
  const [copied, setCopied] = useState(false);
  const [savingImg, setSavingImg] = useState(false);

  const intro = useMemo(() => buildIntro(guestName?.trim()), [guestName]);

  const plainText = useMemo(() => {
    const parts = [];
    parts.push("SOMA LINEA — A Take-Home Reading");
    parts.push("");
    parts.push(intro);
    if (overall) parts.push("\n" + overall);
    parts.push("\n— The lines —");
    (lines || []).forEach((l) => {
      parts.push(`\n${l.title} · ${l.subtitle}`);
      parts.push(l.note);
    });
    if (compatibility) {
      parts.push("\nA Reflection on Resonance");
      parts.push(compatibility);
    }
    if (markings?.witch) {
      parts.push("\n— Mystic Markings —");
      parts.push(`\n${markings.witch.title} · ${markings.witch.subtitle}`);
      parts.push(markings.witch.text);
      (markings.gifts || []).forEach((g) => {
        parts.push(`\n${g.title} · ${g.subtitle}`);
        parts.push(g.text);
      });
    }
    parts.push("\n— Adornments (an invitation, not a prescription) —");
    ADORNMENTS.forEach((a) => {
      parts.push(`\n${a.finger} · ${a.planet} — ${a.energy}`);
      parts.push(a.note);
      parts.push(`Stone: ${a.stones} · Metal: ${a.metal}`);
    });
    parts.push("\n" + CLOSING);
    parts.push("\n" + `${HANDS_GRATITUDE.title}\n${HANDS_GRATITUDE.text}`);
    parts.push("\n" + `${PALMISTRY_RESPECT.title}\n${PALMISTRY_RESPECT.text}`);
    parts.push("\n" + SOMA_FOOTER);
    return parts.join("\n");
  }, [intro, overall, lines, compatibility, markings]);

  if (!open) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      setCopied(true);
      toast.success("Copied — it's yours to keep");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy — select and copy the text manually");
    }
  };

  const downloadPdf = () => {
    try {
      buildReadingPdf({ guestName, prints, lines, markings, overall, compatibility });
      toast.success("Your reading is downloading ✦");
    } catch {
      toast.error("Couldn't build the PDF");
    }
  };

  const downloadImage = async () => {
    setSavingImg(true);
    try {
      const blob = await buildMarkImageBlob({ markings, printUri: prints?.[0]?.uri, guestName, overall });
      if (!blob) throw new Error("no blob");
      downloadBlob(blob, `soma-linea-${slug(guestName)}.png`);
      toast.success("Image saved ✦");
    } catch {
      toast.error("Couldn't build the image");
    } finally {
      setSavingImg(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/70 p-4 py-8" data-testid="report-modal">
      <div className="parch-texture relative w-full max-w-lg rounded-2xl border border-parch-line shadow-2xl">
        {/* Sticky header */}
        <div className="sticky top-0 z-10 flex items-center justify-between rounded-t-2xl border-b border-parch-line bg-parch-bg/95 px-6 py-4 backdrop-blur">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-parch-accent">// the keepsake</p>
          <button onClick={onClose} data-testid="report-close" className="text-parch-inkSoft hover:text-parch-ink">
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 md:px-10">
          <p className="text-center font-mono text-[10px] uppercase tracking-[0.3em] text-parch-accent">
            Soma Linea · a take-home reading
          </p>
          <h2 className="mt-2 text-center font-display text-4xl italic leading-tight text-parch-ink">
            {guestName?.trim() ? `${guestName.trim()}'s palms` : "The story in your palms"}
          </h2>

          {/* Prints */}
          {prints?.length > 0 && (
            <div className="mt-6 flex justify-center gap-3">
              {prints.map((p, i) => (
                <div key={i} className="relative flex h-44 w-32 items-center justify-center overflow-hidden rounded-lg border border-parch-line bg-parch-card">
                  <img src={p.uri} alt={p.label} className="h-full w-full object-contain" />
                  {!p.ink && <div className="absolute inset-0 bg-[rgba(32,24,14,0.28)]" />}
                </div>
              ))}
            </div>
          )}

          {/* Intro */}
          <p className="mt-6 leading-relaxed text-parch-ink">{intro}</p>

          {overall && (
            <p className="mt-4 font-display text-xl italic leading-relaxed text-parch-ink">{overall}</p>
          )}

          {/* Lines */}
          <div className="mt-6 space-y-5">
            {(lines || []).map((l) => {
              const color = lineColor(l.key);
              return (
                <div key={l.key} className="pl-4" style={{ borderLeft: `4px solid ${color}` }}>
                  <p className="font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">{l.subtitle}</p>
                  <p className="font-display text-2xl" style={{ color }}>{l.title}</p>
                  <p className="mt-1 leading-relaxed" style={{ color: lineInk(l.key) }}>{l.note}</p>
                </div>
              );
            })}
          </div>

          {compatibility && (
            <div className="mt-6 rounded-xl border border-parch-line bg-[#E9DCC0] p-5">
              <p className="font-display text-2xl text-parch-ink">A Reflection on Resonance</p>
              <p className="mt-1 leading-relaxed text-parch-ink">{compatibility}</p>
            </div>
          )}

          <MysticMarkings markings={markings} />

          {markings?.witch && (
            <div className="mt-4">
              <ShareMark markings={markings} printUri={prints?.[0]?.uri} guestName={guestName} overall={overall} testId="keepsake-share-mark" />
            </div>
          )}

          <AdornmentsSection />

          {/* Grounding — the hands that carried you */}
          <div className="mt-8 rounded-xl border border-parch-line bg-parch-card p-5">
            <p className="font-display text-2xl text-parch-ink">{HANDS_GRATITUDE.title}</p>
            <p className="mt-2 leading-relaxed text-parch-ink">{HANDS_GRATITUDE.text}</p>
          </div>
          <div className="mt-3 rounded-xl border border-parch-line bg-[#E9DCC0] p-5">
            <p className="font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">// {PALMISTRY_RESPECT.title}</p>
            <p className="mt-2 leading-relaxed text-parch-ink">{PALMISTRY_RESPECT.text}</p>
          </div>

          {/* Closing */}
          <p className="mt-8 whitespace-pre-line leading-relaxed text-parch-ink">{CLOSING}</p>
          <p className="mt-6 border-t border-parch-line pt-4 text-center font-mono text-[10px] uppercase tracking-wide text-parch-inkSoft">
            {SOMA_FOOTER}
          </p>

          {/* Download the keepsake */}
          <div className="mt-6 border-t border-parch-line pt-5">
            <p className="font-mono text-[10px] uppercase tracking-wide text-parch-accent">// take it with you</p>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row">
              <button
                onClick={downloadPdf}
                data-testid="keepsake-download-pdf"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-parch-accent px-5 py-3 font-bold text-white hover:opacity-90"
              >
                <FileDown size={18} /> Download PDF
              </button>
              <button
                onClick={downloadImage}
                disabled={savingImg}
                data-testid="keepsake-download-image"
                className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-parch-accent px-5 py-3 font-bold text-parch-accent hover:bg-parch-card disabled:opacity-60"
              >
                <ImageDown size={18} /> {savingImg ? "Saving…" : "Download image"}
              </button>
            </div>
          </div>
        </div>

        {/* Sticky actions */}
        <div className="sticky bottom-0 flex gap-3 rounded-b-2xl border-t border-parch-line bg-parch-bg/95 px-6 py-4 backdrop-blur">
          <button
            onClick={copy}
            data-testid="report-copy-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-parch-ink py-3 font-bold text-parch-ink hover:bg-parch-card"
          >
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied ? "Copied" : "Copy the text"}
          </button>
          <button
            onClick={onFinish}
            data-testid="report-finish-button"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-parch-ink py-3 font-bold text-parch-bg hover:opacity-90"
          >
            Finish reading
          </button>
        </div>
      </div>
    </div>
  );
}
